import { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard, Trash2, Clock, Sparkles,
  CheckCircle2, ChevronLeft, ChevronRight
} from "lucide-react";
import DashboardLayout from "../../components/DashboardLayout";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";

const PAGE_SIZE = 20;

function getToday() { return new Date().toISOString().split("T")[0]; }
// function addDays(dateStr, n) {
//   const d = new Date(dateStr + "T00:00:00");
//   d.setDate(d.getDate() + n);
//   return d.toISOString().split("T")[0];
// }

function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d); // local time, no timezone shift
  date.setDate(date.getDate() + n);
  const yy = date.getFullYear();
  const mm  = String(date.getMonth() + 1).padStart(2, "0");
  const dd  = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
function formatDate(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}
function formatTime(iso) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function formatFullDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function DateNavigator({ date, onChange }) {
  const isToday = date === getToday();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <button onClick={() => onChange(addDays(date, -1))} style={{ width: 32, height: 32, borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <ChevronLeft size={16} color="var(--mut)" />
      </button>
      <div style={{ padding: "6px 14px", borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", fontSize: 13, fontWeight: 600, color: "var(--ink)", minWidth: 140, textAlign: "center" }}>
        {isToday ? `Today · ${formatDate(date)}` : formatDate(date)}
      </div>
      <button onClick={() => onChange(addDays(date, 1))} disabled={isToday} style={{ width: 32, height: 32, borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: isToday ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: isToday ? 0.4 : 1 }}>
        <ChevronRight size={16} color="var(--mut)" />
      </button>
      {!isToday && (
        <button onClick={() => onChange(getToday())} style={{ padding: "6px 12px", borderRadius: 8, border: "1.5px solid var(--g300)", background: "var(--g50)", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "var(--g600)" }}>Today</button>
      )}
    </div>
  );
}

export default function ResidentHistory() {
  const { profile } = useAuth();
  const navigate    = useNavigate();
  const propertyId  = profile?.property_id;
  const unitNumber  = profile?.unit_number;
  const userId      = profile?.id;
  const today       = getToday();

  const NAV = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/resident/dashboard", active: true },
  { label: "My Trash",  icon: Trash2,  path: "/resident/my-trash" },
  { label: "History",   icon: Clock,   path: "/resident/history" },
  { label: "Services",  icon: Sparkles, onClick: () => navigate("/resident/services") },
];

  const [view,         setView]         = useState("date");  // "date" | "all"
  const [selectedDate, setSelectedDate] = useState(today);
  const [logs,         setLogs]         = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [totalEver,    setTotalEver]    = useState(0);

  // Fetch all-time count
  useEffect(() => {
    if (!propertyId || !userId) return;
    supabase.from("activity_logs")
      .select("*", { count: "exact", head: true })
      .eq("property_id", propertyId).eq("user_id", userId)
      .eq("action", "resident_trash_submitted")
      .then(({ count }) => setTotalEver(count || 0));
  }, [propertyId, userId]);

  const fetchLogs = useCallback(async () => {
    if (!propertyId || !userId) return;
    setLoading(true);

    let query = supabase
      .from("activity_logs")
      .select("id, created_at, metadata", { count: "exact" })
      .eq("property_id", propertyId)
      .eq("user_id", userId)
      .eq("action", "resident_trash_submitted")
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (view === "date") {
      query = query
        .gte("created_at", `${selectedDate}T00:00:00`)
        .lte("created_at", `${selectedDate}T23:59:59`);
    }

    const { data, count } = await query;
    setLogs(data || []);
    setTotal(count || 0);
    setLoading(false);
  }, [propertyId, userId, view, selectedDate, page]);

  useEffect(() => { setPage(0); }, [view, selectedDate]);
  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Group by date for "all" view
  const groupedLogs = {};
  if (view === "all") {
    logs.forEach(log => {
      const dateKey = log.created_at.split("T")[0];
      if (!groupedLogs[dateKey]) groupedLogs[dateKey] = [];
      groupedLogs[dateKey].push(log);
    });
  }

  return (
    <DashboardLayout role="resident" title="Submission History" navItems={NAV}>

      {/* Header */}
      <div className="fu" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "2px", textTransform: "uppercase", color: "var(--g600)", marginBottom: 6 }}>Resident Portal</div>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 500, color: "var(--ink)", margin: 0 }}>History</h2>
        <p style={{ fontSize: 13, color: "var(--mut)", marginTop: 4 }}>
          Unit <strong style={{ color: "var(--g700)" }}>#{unitNumber || "—"}</strong> · <strong style={{ color: "var(--g700)" }}>{totalEver}</strong> total submissions
        </p>
      </div>

      {/* View Toggle + Date Navigator */}
      <div className="fu d1" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        {/* Toggle */}
        <div style={{ display: "flex", background: "#f5f5f5", borderRadius: 10, padding: 3, gap: 3 }}>
          {[{ key: "date", label: "By Date" }, { key: "all", label: "All Time" }].map(({ key, label }) => (
            <button key={key} onClick={() => setView(key)} style={{
              padding: "7px 14px", borderRadius: 8, border: "none",
              background: view === key ? "#fff" : "transparent",
              color: view === key ? "var(--ink)" : "var(--mut)",
              fontSize: 12, fontWeight: 700, cursor: "pointer",
              boxShadow: view === key ? "0 1px 4px rgba(0,0,0,.08)" : "none",
              transition: "all .15s",
            }}>{label}</button>
          ))}
        </div>

        {/* Date nav — only in date view */}
        {view === "date" && (
          <DateNavigator date={selectedDate} onChange={setSelectedDate} />
        )}
      </div>

      {/* Result count */}
      <div style={{ fontSize: 12, color: "var(--mut)", fontWeight: 600, marginBottom: 12 }}>
        {loading ? "Loading…" : `${total} submission${total !== 1 ? "s" : ""} ${view === "date" ? `on ${formatDate(selectedDate)}` : "total"}`}
      </div>

      {/* Logs */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "var(--mut)", fontSize: 13 }}>Loading…</div>
      ) : logs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "36px", background: "#fff", borderRadius: 14, border: "1.5px dashed var(--ln)" }}>
          <Trash2 size={28} color="var(--ln)" style={{ marginBottom: 10 }} />
          <div style={{ fontSize: 13, color: "var(--mut)" }}>
            {view === "date" ? `No submissions on ${formatDate(selectedDate)}.` : "No submission history yet."}
          </div>
        </div>
      ) : view === "date" ? (
        // Date view — simple list
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {logs.map((log, i) => (
            <div key={log.id} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 16px", borderRadius: 11,
              background: "#fff", border: "1px solid var(--ln)",
              boxShadow: "0 1px 3px rgba(0,0,0,.04)",
            }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--g50)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <CheckCircle2 size={16} color="var(--g600)" strokeWidth={2} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Trash submitted — Unit #{unitNumber}</div>
                <div style={{ fontSize: 11.5, color: "var(--mut)", marginTop: 2 }}>{formatTime(log.created_at)}</div>
              </div>
              {i === 0 && selectedDate === today && (
                <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 20, background: "var(--g600)", color: "#fff", textTransform: "uppercase", letterSpacing: ".5px" }}>Latest</span>
              )}
            </div>
          ))}
        </div>
      ) : (
        // All view — grouped by date
        Object.entries(groupedLogs).map(([dateKey, entries]) => (
          <div key={dateKey} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mut)", textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
              <span>{formatFullDate(dateKey + "T00:00:00")}</span>
              <span style={{ fontSize: 10, background: "var(--g50)", color: "var(--g600)", border: "1px solid var(--g200)", borderRadius: 10, padding: "1px 7px", fontWeight: 700 }}>
                {entries.length}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              {entries.map(log => (
                <div key={log.id} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "11px 14px", borderRadius: 10,
                  background: "#fff", border: "1px solid var(--ln)",
                }}>
                  <CheckCircle2 size={15} color="var(--g600)" strokeWidth={2} />
                  <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>Unit #{unitNumber}</div>
                  <div style={{ fontSize: 12, color: "var(--mut)" }}>{formatTime(log.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: page === 0 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, color: "var(--mut)", opacity: page === 0 ? 0.4 : 1 }}>← Prev</button>
          <span style={{ fontSize: 13, color: "var(--mut)", fontWeight: 600 }}>Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} style={{ padding: "8px 16px", borderRadius: 8, border: "1.5px solid var(--ln)", background: "#fff", cursor: page >= totalPages - 1 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, color: "var(--mut)", opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next →</button>
        </div>
      )}

      <style>{`@keyframes fadeUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </DashboardLayout>
  );
}